<template>
  <div class="main-container">
    <!-- Header Section -->
    <v-row class="pa-2 header-row">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <!-- Back Button -->
        <v-btn icon class="back-button my-4 mx-4" @click="confirmBackNavigation">
          <v-icon class="white--text mx-4">mdi-arrow-left</v-icon>
        </v-btn>

        <!-- Add Job Button -->
        <v-btn
          v-if="(isTechnician || !isSalesperson) && canAddJob"
          color="#ff3b47"
          class="add-job-button my-4 mx-4"
          dark
          @click="handleAddJobClick"
        >
          Add Job
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loader -->
    <v-row v-if="loading" justify="center" align="center" class="loader-row">
      <v-progress-circular indeterminate color="#ff3b47"></v-progress-circular>
    </v-row>

    <!-- Jobs Grid -->
    <div v-if="!loading" class="jobs-container">
      <!-- Search & Filter -->
      <v-row justify="center" class="search-filter-row">
        <v-col cols="12" md="4">
          <v-text-field
            class="pa-3 white-search-field"
            filled dense rounded
            v-model="search"
            append-icon="mdi-magnify"
            label="Search Jobs"
            single-line hide-details
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedStatus"
            :items="[...statusOptions, 'Clear filter']"
            placeholder="Filter by Status"
            filled dense rounded class="my-3"
            @change="handleSelectChange"
          />
        </v-col>
      </v-row>

      <!-- Job Cards -->
      <v-row>
        <v-col
          v-for="job in paginatedJobs"
          :key="job.id"
          cols="12" sm="6" md="4" lg="3"
        >
          <v-card
            class="modern-card"
            elevation="6" hover
            @click="canEditJob && handleCardClick(job)"
          >
            <!-- Chips & Info Button -->
            <v-row class="d-flex justify-end pr-3 pt-3 align-center">
              <v-chip :color="getChipColor(job.status)" dark label class="status-chip ml-2">
                {{ job.status || 'Unknown' }}
              </v-chip>
              <v-chip color="grey" dark label class="job-number-chip ml-2">
                Job #: {{ job.jobNumber || 'N/A' }}
              </v-chip>
              <v-btn icon @click.stop="openExtraInfo(job)" title="Preview / Download" class="ml-2">
                <v-icon color="#ff3b47">mdi-information</v-icon>
              </v-btn>
            </v-row>

            <!-- Card Body -->
            <v-card-text class="pa-4">
              <v-row><v-col class="py-2"><span class="text-caption">Customer:</span>
                <span class="font-weight-medium">{{ job.customerName }}</span></v-col></v-row>
              <v-row><v-col class="py-2"><span class="text-caption">Seal Type:</span>
                <span class="font-weight-medium">{{ job.jobName }}</span></v-col></v-row>
              <v-row><v-col class="py-2"><span class="text-caption">Created:</span>
                <span class="font-weight-medium">{{ formatDate(job.currentDate) }}</span></v-col></v-row>
              <v-row><v-col class="py-2"><span class="text-caption">Total:</span>
                <span class="font-weight-medium">{{ formatCurrency(job.grandTotal) }}</span></v-col></v-row>
            </v-card-text>

            <!-- Open Job Button -->
            <v-card-actions v-if="canEditJob" class="d-flex justify-between align-center">
              <v-btn
                rounded dark color="#ff3b47"
                @click.stop="handleCardClick(job)"
                class="open-job-button flex-grow-1 mr-2"
              >
                Open Job
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Load More -->
      <v-row justify="center" v-if="canLoadMore">
        <v-btn color="#ff3b47" @click="loadMore" class="load-more-button">
          Load More
        </v-btn>
      </v-row>
    </div>

    <!-- Back Confirmation -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Navigation</v-card-title>
        <v-card-text>Are you sure you want to go back? Unsaved changes might be lost.</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="#ff3b47" text @click="backDialog = false">Cancel</v-btn>
          <v-btn color="#28a745" text @click="goBack">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Extra-Info Dialog -->
    <v-dialog v-model="extraInfoDialog.visible" max-width="600" class="extra-info-dialog">
      <v-card>
        <v-card-title class="headline dialog-title">
          <!-- Single combined download button -->
          <v-btn icon @click="downloadJobPdf(extraInfoJob)" title="Download PDF">
            <v-icon color="#ff3b47">mdi-download</v-icon>
          </v-btn>
          Job Preview
          <v-spacer/>
          <v-btn icon @click="extraInfoDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Job #:</strong> {{ extraInfoJob.jobNumber }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Customer:</strong> {{ extraInfoJob.customerName }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Type:</strong> {{ extraInfoJob.jobName }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Size:</strong> {{ extraInfoJob.ShaftSize }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Materials:</strong>
              {{
                [extraInfoJob.elastomers, extraInfoJob.ibs, extraInfoJob.ibr,
                 extraInfoJob.obs, extraInfoJob.obr].filter(Boolean).join(", ")
              }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Cust Ref:</strong> {{ extraInfoJob.customerReference }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Summary:</strong> {{ extraInfoJob.JobSummary }}
            </v-list-item-title></v-list-item-content></v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, doc, getDoc, query, where
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { jsPDF } from "jspdf";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  name: "AllJobs",
  data() {
    return {
      backDialog: false,
      jobs: [],
      search: "",
      selectedStatus: "",
      statusOptions: [
        "Inspection", "Costing Complete", "Quoted",
        "InProgress", "Ready", "Completed",
        "Delayed", "Canceled"
      ],
      userRole: "",
      userPermissions: {},
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      visibleCount: 12,
      loadIncrement: 12,
      loading: false,
      extraInfoDialog: { visible: false },
      extraInfoJob: {}
    };
  },

  computed: {
    isSalesperson() { return this.userRole === "Salesperson"; },
    isTechnician() { return this.userRole === "Technician"; },
    canAddJob() { return !!this.userPermissions.Add_Button; },
    canEditJob() { return !!this.userPermissions.Edit_Details; },

    filteredJobs() {
      let f = Array.isArray(this.jobs) ? [...this.jobs] : [];
      if (this.search) {
        const q = this.search.toLowerCase();
        f = f.filter(j =>
          j.jobName?.toLowerCase().includes(q) ||
          String(j.jobNumber).toLowerCase().includes(q) ||
          j.customerName?.toLowerCase().includes(q)
        );
      }
      if (this.selectedStatus && this.selectedStatus !== "Clear filter") {
        f = f.filter(j => j.status === this.selectedStatus);
      }
      return f.sort((a, b) => (+b.jobNumber || 0) - (+a.jobNumber || 0));
    },
    paginatedJobs() { return this.filteredJobs.slice(0, this.visibleCount); },
    canLoadMore() { return this.visibleCount < this.filteredJobs.length; }
  },

  watch: {
    search() { this.resetPagination(); },
    selectedStatus() { this.resetPagination(); }
  },

  async created() {
    this.loading = true;
    await this.fetchUserRole();
    await this.fetchJobs();
    this.loading = false;
  },

  methods: {
    /** NAVIGATION **/
    confirmBackNavigation() { this.backDialog = true; },
    goBack() { this.backDialog = false; this.$router.go(-1); },

    /** USER ROLE **/
    async fetchUserRole() {
      const current = auth.currentUser;
      if (current) return this.loadUserDocument(current.uid);

      await new Promise(resolve => {
        onAuthStateChanged(auth, user => {
          if (user) this.loadUserDocument(user.uid).finally(resolve);
          else resolve();
        });
      });
    },
    async loadUserDocument(uid) {
      try {
        const uSnap = await getDoc(doc(db, "users", uid));
        if (uSnap.exists()) {
          const d = uSnap.data();
          this.userRole = d.role || "";
          this.userPermissions = d.jobs || {};
        }
      } catch (e) {
        console.error("User doc error:", e);
        this.userRole = "Guest";
        this.userPermissions = {};
        this.showSnackbar("Failed to load user permissions", "error");
      }
    },

    /** FETCH JOBS **/
    async fetchJobs() {
      try {
        const snap = await getDocs(collection(db, "jobs"));
        this.jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        console.error("Fetch jobs error:", e);
        this.showSnackbar("Failed to fetch jobs", "error");
      }
    },

    /** CARD ACTIONS **/
    handleCardClick(job) {
      if (!this.canEditJob) return;
      localStorage.setItem("selectedJob", job.id);
      const routes = {
        Salesperson: "/SalesVB",
        Technician: "/TechVB",
        Engineer: "/ViewJob"
      };
      this.$router.push(routes[this.userRole] || { name: "ViewJob" });
    },
    handleAddJobClick() {
      if (!this.canAddJob) return;
      this.$router.push(this.isTechnician ? "/TechAddJob" : "/addJob");
    },

    /** FILTER / PAGINATION **/
    clearFilters() { this.search = ""; this.selectedStatus = ""; },
    handleSelectChange(v) { if (v === "Clear filter") this.clearFilters(); },
    loadMore() { this.visibleCount += this.loadIncrement; },
    resetPagination() { this.visibleCount = this.loadIncrement; },

    /** UI HELPERS **/
    getChipColor(status) {
      const map = {
        "job order": "blue",
        inspection: "orange",
        "costing complete": "teal",
        quoted: "purple",
        inprogress: "light-blue",
        ready: "green",
        completed: "dark-green",
        delayed: "brown",
        canceled: "red"
      };
      return map[status?.toLowerCase()] || "grey";
    },
    formatDate(s) {
      const d = new Date(s);
      return isNaN(d) ? s : d.toLocaleDateString(undefined,{ year:"numeric", month:"short", day:"numeric"});
    },
    formatCurrency(a) {
      return new Intl.NumberFormat("en-ZA",{style:"currency",currency:"ZAR"}).format(+a||0);
    },
    showSnackbar(msg, color="success") { this.snackbarMessage=msg; this.snackbarColor=color; this.snackbar=true; },

    /** EXTRA-INFO **/
    openExtraInfo(job) { this.extraInfoJob = job; this.extraInfoDialog.visible = true; },

    /** SINGLE PDF DOWNLOAD (details + photos) **/
    async downloadJobPdf(job) {
      if (!job?.jobNumber) { this.showSnackbar("Invalid job.", "error"); return; }

      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const margin = 20;
      let y = margin;
      const pageWidth = 210;
      const pageHeight = 297;
      const usableWidth = pageWidth - 2 * margin;
      const sectionSpacing = 12;
      const labelColor = [255, 59, 71]; // #ff3b47
      const lineSpacing = 8;
      const minBottomMargin = 18;
      const labelWidth = 38;
      const valueX = margin + labelWidth + 2;

      // Helper to check if there's enough space, else add page
      const ensureSpace = (neededHeight) => {
        if (y + neededHeight > pageHeight - minBottomMargin) {
          pdf.addPage();
          y = margin;
        }
      };

      // Centered logo
      try {
        const logoUrl = require("@/assets/pdflogo.jpg");
        const img = new window.Image();
        img.src = logoUrl;
        await new Promise(resolve => { img.onload = resolve; });
        const logoW = 80, logoH = 28; // Stretched and larger logo
        const logoX = (pageWidth - logoW) / 2;
        pdf.addImage(img, "JPEG", logoX, y, logoW, logoH);
        y += logoH + 10; // More space below logo
      } catch (e) {}

      // Centered title
      ensureSpace(14);
      pdf.setFontSize(22);
      pdf.setTextColor(...labelColor);
      pdf.setFont(undefined, "bold");
      pdf.text(`Job #${job.jobNumber} Report`, pageWidth / 2, y, { align: "center" });
      y += 8;
      // Colored thick divider
      pdf.setDrawColor(...labelColor);
      pdf.setLineWidth(1.5);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 6;

      // Job Info Table
      pdf.setFontSize(13);
      pdf.setFont(undefined, "bold");
      const info = [
        ["Customer", job.customerName],
        ["Type", job.jobName],
        ["Size", job.ShaftSize],
        ["Materials", [job.elastomers, job.ibs, job.ibr, job.obs, job.obr].filter(Boolean).join(", ")],
        ["Cust Ref", job.customerReference],
      ];
      for (const [label, value] of info) {
        ensureSpace(lineSpacing);
        pdf.setTextColor(...labelColor);
        pdf.text(`${label}:`, margin, y);
        pdf.setFont(undefined, "normal");
        pdf.setTextColor(0, 0, 0);
        pdf.text(String(value || "-"), valueX, y);
        pdf.setFont(undefined, "bold");
        y += lineSpacing;
      }
      // Summary
      ensureSpace(lineSpacing + 2);
      pdf.setTextColor(...labelColor);
      pdf.text("Summary:", margin, y);
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(0, 0, 0);
      const summaryLines = pdf.splitTextToSize(job.JobSummary || "-", usableWidth - labelWidth - 10);
      ensureSpace(summaryLines.length * 6 + 2);
      pdf.text(summaryLines, valueX, y);
      y += summaryLines.length * 6 + 2;
      // Light gray divider
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(margin, y, pageWidth - margin, y);
      y += sectionSpacing;

      // Helper to add images from a given storage path
      const addImagesFromPath = async (folderPath, sectionLabel) => {
        try {
          const folderRef = ref(storage, folderPath);
          const list = await listAll(folderRef);
          // Filter for image files (png, jpg, jpeg, webp)
          const imageItems = list.items.filter(item =>
            /\.(png|jpe?g|webp)$/i.test(item.name)
          );
          if (imageItems.length > 0) {
            ensureSpace(16);
            // Section header
            pdf.setFontSize(15);
            pdf.setFont(undefined, "bold");
            pdf.setTextColor(...labelColor);
            pdf.text(sectionLabel, margin, y);
            y += 6;
            pdf.setDrawColor(...labelColor);
            pdf.setLineWidth(1);
            pdf.line(margin, y, pageWidth - margin, y);
            y += 4;
            pdf.setFont(undefined, "normal");
            pdf.setTextColor(0, 0, 0);
            pdf.setLineWidth(0.5);
          }
          let imgCount = 1;
          for (const itemRef of imageItems) {
            const url = await getDownloadURL(itemRef);
            const imgData = await this.urlToDataUrl(url);
            const imgProps = pdf.getImageProperties(imgData);
            // Fit image to width, but max height 90mm
            let imgW = usableWidth - 10;
            let imgH = (imgProps.height * imgW) / imgProps.width;
            if (imgH > 90) {
              imgH = 90;
              imgW = (imgProps.width * imgH) / imgProps.height;
            }
            ensureSpace(imgH + 16);
            // Draw border for image
            pdf.setDrawColor(220, 220, 220);
            pdf.rect((pageWidth - imgW) / 2 - 1, y - 1, imgW + 2, imgH + 2);
            pdf.addImage(imgData, "JPEG", (pageWidth - imgW) / 2, y, imgW, imgH);
            y += imgH + 3;
            // Optional caption
            pdf.setFontSize(11);
            pdf.setTextColor(120, 120, 120);
            pdf.text(`Image ${imgCount}`, pageWidth / 2, y, { align: "center" });
            y += 7;
            pdf.setTextColor(0, 0, 0);
            imgCount++;
          }
          if (imageItems.length > 0) {
            y += 2;
          }
        } catch (e) {
          // Folder may not exist or be empty, skip
        }
      };

      // Add images from base, supplierQuotes, and additionalInfo
      await addImagesFromPath(`jobs/${job.jobNumber}`, "Main Job Images");
      await addImagesFromPath(`jobs/${job.jobNumber}/supplierQuotes`, "Supplier Quotes");
      await addImagesFromPath(`jobs/${job.jobNumber}/additionalInfo`, "Additional Info");

      // Checklist Photos Section
      const checklistPhotos = await this.fetchChecklistPhotos(job.jobNumber);
      if (checklistPhotos.length > 0) {
        ensureSpace(16);
        pdf.setFontSize(15);
        pdf.setFont(undefined, "bold");
        pdf.setTextColor(...labelColor);
        pdf.text("Checklist Photos", margin, y);
        y += 6;
        pdf.setDrawColor(...labelColor);
        pdf.setLineWidth(1);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 4;
        pdf.setFont(undefined, "normal");
        pdf.setTextColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        // Grid layout: 4 per row
        const photosPerRow = 4;
        const spacing = 5; // space between photos
        const maxImgW = (usableWidth - (photosPerRow - 1) * spacing) / photosPerRow;
        const maxImgH = 40;
        let row = [];
        for (let i = 0; i < checklistPhotos.length; i++) {
          try {
            const url = checklistPhotos[i];
            const imgData = await this.urlToDataUrl(url);
            const imgProps = pdf.getImageProperties(imgData);
            let imgW = maxImgW;
            let imgH = (imgProps.height * imgW) / imgProps.width;
            if (imgH > maxImgH) {
              imgH = maxImgH;
              imgW = (imgProps.width * imgH) / imgProps.height;
            }
            row.push({ imgData, imgW, imgH });
            // If row is full or last image, render the row
            if (row.length === photosPerRow || i === checklistPhotos.length - 1) {
              // Find tallest image in row
              const rowHeight = Math.max(...row.map(img => img.imgH));
              ensureSpace(rowHeight + 10);
              // Calculate total width of images + spacing
              const totalRowWidth = row.reduce((sum, img) => sum + img.imgW, 0) + spacing * (row.length - 1);
              let x = margin + (usableWidth - totalRowWidth) / 2;
              for (const img of row) {
                pdf.setDrawColor(220, 220, 220);
                pdf.rect(x - 1, y - 1, img.imgW + 2, img.imgH + 2);
                pdf.addImage(img.imgData, "JPEG", x, y, img.imgW, img.imgH);
                x += img.imgW + spacing;
              }
              y += rowHeight + 7;
              row = [];
            }
          } catch (e) {
            // skip failed image
          }
        }
        y += 2;
      }

      // Add page numbers to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 5, { align: "right" });
        pdf.setTextColor(0, 0, 0);
      }

      pdf.save(`job_${job.jobNumber}.pdf`);
      this.showSnackbar("PDF downloaded.");
    },
    urlToDataUrl(url) {
      return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const c = document.createElement("canvas");
          c.width = img.width; c.height = img.height;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/jpeg"));
        };
        img.src = url;
      });
    },
    async fetchChecklistPhotos(jobNumber) {
      const db = getFirestore(firebaseApp);
      const checklistRef = collection(db, "checklists");
      const q = query(checklistRef, where("jobNumber", "==", jobNumber));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return [];
      const checklist = querySnapshot.docs[0].data().checklist || [];
      // Flatten all section.photos arrays
      return checklist.flatMap(section => section.photos || []);
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

.main-container { background:#121212; min-height:100vh; }
/* header */
.header-row { background:#1e1e1e; border-bottom:1px solid #333; }
/* cards container */
.jobs-container { background:#fff; border-radius:20px 20px 0 0; padding:24px; box-shadow:0 4px 15px rgba(0,0,0,.1); }
/* search row */
.search-filter-row { margin-bottom:24px; }
.white-search-field .v-input__control { background:#fff; border-radius:8px; }
/* card */
.modern-card { background:#fff; border-radius:16px; transition:.3s; cursor:pointer; margin:8px; height:260px; display:flex; flex-direction:column; justify-content:space-between; }
.modern-card:hover { transform:translateY(-5px); box-shadow:0 8px 25px rgba(0,0,0,.2); }
/* chips */
.status-chip,.job-number-chip { font-size:.75rem; text-transform:capitalize; }
/* open btn */
.open-job-button { font-weight:600; transition:.3s; }
.open-job-button:hover { background:#e63946; transform:scale(1.02); }
/* misc buttons */
.back-button { color:#fff; border-radius:50%; transition:.3s; }
.back-button:hover { transform:scale(1.1); }
.add-job-button,
.load-more-button { font-weight:600; transition:.3s; }
.add-job-button:hover,
.load-more-button:hover { background:#e63946; transform:scale(1.02); }
.loader-row { margin-top:50px; }
/* dialog */
.extra-info-dialog .v-card { border-radius:16px; }
</style>
